import { Shield, Lock, Eye, UserCheck, FileText, Bell } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-6 md:py-12 pb-20 md:pb-12">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl md:rounded-2xl p-5 md:p-8 mb-6 md:mb-8 text-white">
          <div className="flex items-center mb-3 md:mb-4">
            <Shield className="h-8 w-8 md:h-12 md:w-12 mr-3 md:mr-4" />
            <div>
              <h1 className="text-2xl md:text-4xl font-bold">Privacy Policy</h1>
              <p className="text-primary-100 text-xs md:text-base mt-1 md:mt-2">Last updated: October 16, 2025</p>
            </div>
          </div>
          <p className="text-sm md:text-lg text-primary-50">
            Your privacy is important to us. This policy explains how RAPSAPP collects, uses, and protects your personal information.
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-8 space-y-6 md:space-y-8">
          
          {/* Section 1 */}
          <section>
            <div className="flex items-start mb-3 md:mb-4">
              <FileText className="h-5 w-5 md:h-6 md:w-6 text-primary-600 mr-2 md:mr-3 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">1. Information We Collect</h2>
                <div className="text-gray-700 space-y-2 md:space-y-3 text-sm md:text-base">
                  <p>We collect information that you provide directly to us, including:</p>
                  <ul className="list-disc list-inside space-y-1.5 md:space-y-2 ml-2 md:ml-4 text-sm md:text-base">
                    <li><strong>Personal Information:</strong> Name, email address, phone number, and address</li>
                    <li><strong>Account Information:</strong> Username, password, and profile details</li>
                    <li><strong>Service Information:</strong> Service requests, booking details, and preferences</li>
                    <li><strong>Payment Information:</strong> Payment method details (processed securely through third-party providers)</li>
                    <li><strong>Communication Data:</strong> Messages, reviews, and feedback you provide</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="border-t pt-6 md:pt-8">
            <div className="flex items-start mb-3 md:mb-4">
              <Eye className="h-5 w-5 md:h-6 md:w-6 text-primary-600 mr-2 md:mr-3 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">2. How We Use Your Information</h2>
                <div className="text-gray-700 space-y-2 md:space-y-3 text-sm md:text-base">
                  <p>We use the information we collect to:</p>
                  <ul className="list-disc list-inside space-y-1.5 md:space-y-2 ml-2 md:ml-4 text-sm md:text-base">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Process your bookings and transactions</li>
                    <li>Send you service updates, confirmations, and notifications</li>
                    <li>Respond to your comments, questions, and customer service requests</li>
                    <li>Connect you with verified service professionals</li>
                    <li>Improve user experience and personalize services</li>
                    <li>Detect, prevent, and address technical issues and fraudulent activities</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="border-t pt-6 md:pt-8">
            <div className="flex items-start mb-3 md:mb-4">
              <Lock className="h-5 w-5 md:h-6 md:w-6 text-primary-600 mr-2 md:mr-3 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">3. Information Security</h2>
                <div className="text-gray-700 space-y-2 md:space-y-3 text-sm md:text-base">
                  <p>We take reasonable measures to protect your personal information from unauthorized access, use, or disclosure:</p>
                  <ul className="list-disc list-inside space-y-1.5 md:space-y-2 ml-2 md:ml-4 text-sm md:text-base">
                    <li>SSL encryption for data transmission</li>
                    <li>Secure servers with regular security audits</li>
                    <li>Limited access to personal information by authorized personnel only</li>
                    <li>Regular security updates and vulnerability assessments</li>
                    <li>Secure payment processing through PCI-DSS compliant providers</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <UserCheck className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Information Sharing</h2>
                <div className="text-gray-700 space-y-3">
                  <p>We do not sell your personal information. We may share your information with:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Service Professionals:</strong> To fulfill your service requests</li>
                    <li><strong>Service Providers:</strong> Third parties who perform services on our behalf (payment processing, hosting, analytics)</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                    <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <Bell className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Your Rights and Choices</h2>
                <div className="text-gray-700 space-y-3">
                  <p>You have the following rights regarding your personal information:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Access:</strong> Request access to your personal information</li>
                    <li><strong>Correction:</strong> Update or correct your information</li>
                    <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                    <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                    <li><strong>Data Portability:</strong> Request a copy of your data</li>
                  </ul>
                  <p className="mt-4">To exercise these rights, please contact us at <a href="mailto:rapsappservice@gmail.com" className="text-primary-600 hover:underline">rapsappservice@gmail.com</a></p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Cookies and Tracking</h2>
            <div className="text-gray-700 space-y-3">
              <p>We use cookies and similar tracking technologies to improve your experience:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Essential cookies for site functionality</li>
                <li>Analytics cookies to understand usage patterns</li>
                <li>Preference cookies to remember your settings</li>
              </ul>
              <p>You can control cookies through your browser settings.</p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Children's Privacy</h2>
            <div className="text-gray-700 space-y-3">
              <p>RAPSAPP is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.</p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Changes to This Policy</h2>
            <div className="text-gray-700 space-y-3">
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.</p>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t pt-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Contact Us</h2>
            <div className="text-gray-700 space-y-2">
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <div className="space-y-1 mt-4">
                <p><strong>Email:</strong> <a href="mailto:rapsappservice@gmail.com" className="text-primary-600 hover:underline">rapsappservice@gmail.com</a></p>
                <p><strong>Phone:</strong> <a href="tel:+917018401540" className="text-primary-600 hover:underline">+91 7018401540</a></p>
                <p><strong>Address:</strong> Himachal Pradesh, India</p>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
